import {ThemeOptions, iconButtonClasses, Breakpoints} from '@mui/material';

import {Tokens} from '../../../../types/generated/Tokens';

export const MuiIconButtons = ({iconbutton}: Tokens, mediaQueries: Breakpoints): ThemeOptions['components'] => {
  const {base, primary, secondary, tertiary, primaryHc, secondaryHc} = iconbutton;

  return {
    MuiIconButton: {
      defaultProps: {
        disableFocusRipple: true
      },
      styleOverrides: {
        root: {
          ...(base && {
            padding: `${base.desktop.padding.top} ${base.desktop.padding.right} ${base.desktop.padding.bottom} ${base.desktop.padding.left}`
          }),
          backgroundColor: 'transparent',
          background: 'transparent',
          // Need this to override MUI default
          transition: 'none',
          [mediaQueries.down('sm')]: {
            height: primary.mobile.height.max,
            width: primary.mobile.width.max,
            paddingTop: primary.mobile.padding.top,
            paddingRight: primary.mobile.padding.right,
            paddingBottom: primary.mobile.padding.bottom,
            paddingLeft: primary.mobile.padding.left
          },
          // need a base color to override MUI default,
          color: tertiary.desktop.icon.color,
          // Need this to override MUI default
          '&:hover': {
            backgroundColor: `transparent`
          },
          /* Tertiary - Styles applied to the root element if `color="info"`
           * AND `className ="iconButtonContained"`
           */
          '&.iconButtonContained': {
            backgroundColor: tertiary.desktop.background.color,
            color: tertiary.desktop.icon.color,
            borderColor: tertiary.desktop.border.color,
            borderStyle: tertiary.desktop.border.style,
            borderWidth: tertiary.desktop.border.width,
            padding: tertiary.desktop.padding.all,
            '&:hover': {
              background: tertiary.desktop.hover.background.color,
              borderWidth: tertiary.desktop.hover.border.width,
              borderColor: tertiary.desktop.hover.border.color,
              color: tertiary.desktop.hover.icon.color
            },
            '&:active': {
              backgroundColor: tertiary.desktop.active.background.color,
              borderColor: tertiary.desktop.active.border.color,
              color: tertiary.desktop.active.icon.color
            }
          }
        },
        /* Primary - Styles applied to the root element if `color="primary"` */
        colorPrimary: {
          // Need this to override MUI default
          '&:hover': {
            backgroundColor: `transparent`
          },
          /* Primary - Styles applied to the root element if `color="primary"`
           * AND `className ="iconButtonContained"`
           */
          '&.iconButtonContained': {
            backgroundColor: primary.desktop.background.color,
            borderColor: primary.desktop.border.color,
            borderStyle: primary.desktop.border.style,
            borderWidth: primary.desktop.border.width,
            color: primary.desktop.icon.color,
            padding: primary.desktop.padding.all,
            '&:hover': {
              backgroundColor: primary.desktop.hover.background.color,
              borderColor: primary.desktop.hover.border.color,
              borderWidth: primary.desktop.hover.border.width,
              color: primary.desktop.hover.icon.color
            },
            '&:active': {
              backgroundColor: primary.desktop.active.background.color,
              borderColor: primary.desktop.active.border.color,
              boxShadow: 'none'
            }
          },
          /* Primary - Styles applied to the root element if `color="primary"`
           * AND `className ="iconButtonContainedHC"`
           */
          '&.iconButtonContainedHC': {
            backgroundColor: primaryHc.desktop.background.color,
            borderColor: primaryHc.desktop.border.color,
            borderStyle: primaryHc.desktop.border.style,
            borderWidth: primaryHc.desktop.border.width,
            color: primaryHc.desktop.icon.color,
            padding: primaryHc.desktop.padding.all,
            '&:hover': {
              backgroundColor: primaryHc.desktop.hover.background.color,
              borderWidth: primaryHc.desktop.hover.border.width,
              borderColor: primaryHc.desktop.hover.border.color,
              color: primaryHc.desktop.hover.icon.color
            },
            '&:active': {
              backgroundColor: primaryHc.desktop.active.background.color,
              borderColor: primaryHc.desktop.active.border.color,
              boxShadow: 'none',
              color: primaryHc.desktop.active.icon.color
            },
            '&.Mui-focusVisible': {
              outlineWidth: primaryHc.ghost.desktop.focus.outline.width,
              outlineStyle: primaryHc.ghost.desktop.focus.outline.style,
              outlineColor: primaryHc.ghost.desktop.focus.outline.color
            }
          },
          /* Primary - Styles applied to the root element if `color="primary"`
           * AND `className ="iconButtonGhostHC"`
           */
          '&.iconButtonGhostHC': {
            borderColor: primaryHc.ghost.desktop.border.color,
            borderStyle: primaryHc.ghost.desktop.border.style,
            borderWith: primaryHc.ghost.desktop.border.width,
            backgroundColor: primaryHc.ghost.desktop.background.color,
            color: primaryHc.ghost.desktop.icon.color,
            padding: primaryHc.ghost.desktop.padding.all,
            '&:hover': {
              backgroundColor: primaryHc.ghost.desktop.hover.background.color,
              borderColor: primaryHc.ghost.desktop.hover.border.color,
              borderWith: primaryHc.ghost.desktop.hover.border.width,
              color: primaryHc.ghost.desktop.hover.icon.color
            },
            '&:active': {
              backgroundColor: primaryHc.ghost.desktop.active.background.color,
              borderColor: primaryHc.ghost.desktop.active.border.color,
              color: primaryHc.ghost.desktop.active.icon.color
            },
            '&.Mui-focusVisible': {
              outlineWidth: primaryHc.ghost.desktop.focus.outline.width,
              outlineStyle: primaryHc.ghost.desktop.focus.outline.style,
              outlineColor: primaryHc.ghost.desktop.focus.outline.color
            },
            [mediaQueries.down('sm')]: {
              maxHeight: primaryHc.ghost.desktop.height.max,
              maxWidth: primaryHc.ghost.desktop.width.max,
              minWidth: primaryHc.ghost.desktop.width.min,
              paddingTop: primaryHc.ghost.desktop.padding.top,
              paddingRight: primaryHc.ghost.desktop.padding.right,
              paddingBottom: primaryHc.ghost.desktop.padding.bottom,
              paddingLeft: primaryHc.ghost.desktop.padding.left
            }
          },
          '&.iconButtonGhost': {
            backgroundColor: primary.ghost.desktop.background.color,
            borderColor: primary.ghost.desktop.border.color,
            borderStyle: primary.ghost.desktop.border.style,
            borderWidth: primary.ghost.desktop.border.width,
            color: primary.ghost.desktop.icon.color,
            padding: primary.ghost.desktop.padding.all,
            '&:hover:not(:focus-within)': {
              backgroundColor: primary.ghost.desktop.hover.background.color,
              borderColor: primary.ghost.desktop.hover.border.color,
              color: primary.ghost.desktop.hover.icon.color
            },
            '&:active:not(:focus-within)': {
              borderColor: primary.ghost.desktop.active.border.color,
              backgroundColor: primary.ghost.desktop.active.background.color,
              color: primary.ghost.desktop.active.icon.color
            },
            [`&.${iconButtonClasses.disabled}`]: {
              backgroundColor: primary.ghost.desktop.disabled.background.color,
              borderColor: primary.ghost.desktop.disabled.border.color
            }
          }
        },
        /* Secondary - Styles applied to the root element when `color="secondary"`. */
        colorSecondary: {
          // Need this to override MUI default
          '&:hover': {
            backgroundColor: `transparent`
          },
          '&.iconButtonContained': {
            backgroundColor: secondary.desktop.background.color,
            borderRadius: secondary.desktop.border.radius,
            borderStyle: secondary.desktop.border.style,
            borderWidth: secondary.desktop.border.width,
            borderColor: secondary.desktop.border.color,
            color: secondary.desktop.icon.color,
            padding: secondary.desktop.padding.all,
            '&:hover': {
              backgroundColor: secondary.desktop.hover.background.color,
              borderWidth: secondary.desktop.hover.border.width,
              borderColor: secondary.desktop.hover.border.color,
              color: secondary.desktop.hover.icon.color
            },
            '&:active': {
              backgroundColor: secondary.desktop.active.background.color,
              borderColor: secondary.desktop.active.border.color,
              boxShadow: 'none',
              color: secondary.desktop.active.icon.color
            },
            '&.Mui-focusVisible': {
              outlineWidth: secondary.desktop.focus.outline.width,
              outlineStyle: secondary.desktop.focus.outline.style,
              outlineColor: secondary.desktop.focus.outline.color
            },
            [`&.${iconButtonClasses.disabled}`]: {
              backgroundColor: secondary.desktop.disabled.background.color,
              borderColor: secondary.desktop.disabled.border.color
            }
          },
          '&.iconButtonContainedHC': {
            backgroundColor: secondaryHc.desktop.background.color,
            borderStyle: secondaryHc.desktop.border.style,
            borderWidth: secondaryHc.desktop.border.width,
            borderColor: secondaryHc.desktop.border.color,
            color: secondaryHc.desktop.icon.color,
            padding: secondaryHc.desktop.padding.all,
            '&:hover': {
              backgroundColor: secondaryHc.desktop.hover.background.color,
              borderWidth: secondaryHc.desktop.hover.border.width,
              borderColor: secondaryHc.desktop.hover.border.color,
              color: secondaryHc.desktop.hover.icon.color
            },
            '&:active': {
              backgroundColor: secondaryHc.desktop.active.background.color,
              borderColor: secondaryHc.desktop.active.border.color,
              boxShadow: 'none',
              color: secondaryHc.desktop.active.icon.color
            },
            '&.Mui-focusVisible': {
              outlineWidth: secondaryHc.desktop.focus.outline.width,
              outlineStyle: secondaryHc.desktop.focus.outline.style,
              outlineColor: secondaryHc.desktop.focus.outline.color
            }
          },
          '&.iconButtonGhost': {
            color: secondary.ghost.desktop.icon.color,
            backgroundColor: secondary.ghost.desktop.background.color,
            borderRadius: secondary.ghost.desktop.border.radius,
            borderColor: secondary.ghost.desktop.border.color,
            borderStyle: secondary.ghost.desktop.border.style,
            borderWidth: secondary.ghost.desktop.border.width,
            padding: secondary.ghost.desktop.padding.all,
            '&:hover': {
              backgroundColor: secondary.ghost.desktop.hover.background.color,
              borderWidth: secondary.ghost.desktop.hover.border.width,
              borderColor: secondary.ghost.desktop.hover.border.color,
              color: secondary.ghost.desktop.hover.icon.color
            },
            '&:active': {
              borderColor: secondary.ghost.desktop.active.border.color,
              backgroundColor: secondary.ghost.desktop.active.background.color,
              color: secondary.ghost.desktop.active.icon.color
            },
            [`&.${iconButtonClasses.disabled}`]: {
              backgroundColor: secondary.ghost.desktop.disabled.background.color,
              borderColor: secondary.ghost.desktop.disabled.border.color
            },
            '&.Mui-focusVisible': {
              outlineWidth: secondary.desktop.focus.outline.width,
              outlineStyle: secondary.desktop.focus.outline.style,
              outlineColor: secondary.desktop.focus.outline.color
            }
          }
        },
        /* Styles applied to the root element if `size="small"`. */
        sizeSmall: {
          [mediaQueries.up('xs')]: {
            ...(base && {
              height: base.desktop.small.height.max,
              width: base.desktop.small.width.max,
              paddingTop: base.desktop.small.padding.top,
              paddingRight: base.desktop.small.padding.right,
              paddingBottom: base.desktop.small.padding.bottom,
              paddingLeft: base.desktop.small.padding.left
            })
          }
        }
      }
    }
  };
};
